<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.jobScheduler',
    path: 'job-scheduler',
    component: 'JobSchedulerConfigurations',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobSchedulerConfigurations',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const openJobSchedulerConfig = (...args) => {
    const [oid] = args;

    return $imodal.value
      .show({
        name: 'JobSchedulerConfig',
        props: { oid },
      })
      .then((res) => {
        console.log(res);
        loadData('items');
      });
  };

  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_editable = computed(() => false);
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'id',
      header: $fn.value.pgettext('JobSchedulerConfig', 'ID'),
      width: '180px',
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <a class="text-nowrap"\n\
     href="#"\n\
     @click.prevent="globs.openJobSchedulerConfig(row.id)"\n\
  >\n\
    {{ value }}\n\
  </a>\n\
</div>\n\
',
      },
    },
    {
      name: 'name',
      header: $fn.value.pgettext('JobSchedulerConfig', 'Name'),
      width: '180px',
    },
    {
      name: 'function_name',
      header: $fn.value.pgettext('JobSchedulerConfig', 'Function'),
      width: '160px',
    },
    {
      name: 'trigger',
      header: $fn.value.pgettext('JobSchedulerConfig', 'Trigger'),
      width: '160px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_editable: b_3343864469950044_editable.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('list_job_scheduler_config')
      .then((items) => _.orderBy(items, ['created_at'], ['asc']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_editable', nodeId: '3343864469950044' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_editable,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_sx2s00cii_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6705964765214521_script_i899amjoh_value_callable = async (
    $event
  ) => {
    return openJobSchedulerConfig();
  };
  const act_6705964765214521_script_i899amjoh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_i899amjoh_value_callable($event);
      },
    };
  };
  const act_8116269939469002_showModal_7agqy5bn8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('import_job_scheduler_config'),
        accept: '.json,.yaml,.yml',
      },
    };
  };
  const act_3343864469950044_script_iyxm0r50e_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('job_scheduler_config').upsert({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_iyxm0r50e_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_iyxm0r50e_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_pms1wnvov_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('job_scheduler_config').delete({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_pms1wnvov_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_pms1wnvov_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_sx2s00cii =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_sx2s00cii_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ik8jlfzzr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_sx2s00cii,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ik8jlfzzr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_71k67n4o5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4ahf9yro5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_71k67n4o5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4ahf9yro5],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_xkjtn9afx =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_i784hxaqh =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_xkjtn9afx,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_i784hxaqh],
      event: 'click.prevent.stop',
    });
  const act_6705964765214521_script_i899amjoh = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_i899amjoh_getActionArgs,
  });
  const inst_evh_6705964765214521_clickPreventStop_b6w6qw13j =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_i899amjoh,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_b6w6qw13j],
      event: 'click.prevent.stop',
    });
  const act_8116269939469002_showModal_7agqy5bn8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8116269939469002_showModal_7agqy5bn8_getActionArgs,
    });
  const inst_evh_8116269939469002_clickPreventStop_s4qg5bpif =
    new actionTypes.EventHandlerImpl({
      action: act_8116269939469002_showModal_7agqy5bn8,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8116269939469002_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8116269939469002_clickPreventStop_s4qg5bpif],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_iyxm0r50e = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_iyxm0r50e_getActionArgs,
  });
  const inst_evh_3343864469950044_save_hh1kzwkya =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_iyxm0r50e,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_hh1kzwkya],
    event: 'save',
  });
  const act_3343864469950044_script_pms1wnvov = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_pms1wnvov_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_gi71v5nir =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_pms1wnvov,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_gi71v5nir],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_ik8jlfzzr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4ahf9yro5,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_i784hxaqh,
    reload: inst_evh_8320016629450276_clickPreventStop_i784hxaqh,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_6705964765214521_clickPreventStop_b6w6qw13j,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_8116269939469002_clickPreventStop_s4qg5bpif,
    inst_evh_8116269939469002_clickPreventStop,
    inst_evh_3343864469950044_save_hh1kzwkya,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_gi71v5nir,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_ik8jlfzzr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ik8jlfzzr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4ahf9yro5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4ahf9yro5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_i784hxaqh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_i784hxaqh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_b6w6qw13j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_b6w6qw13j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop_s4qg5bpif = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop_s4qg5bpif.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_hh1kzwkya = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_hh1kzwkya.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_gi71v5nir = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_gi71v5nir.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="JobSchedulerConfigurations"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3" v-if="loaded">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{
            pgettext(
              'JobSchedulerConfigurations',
              'Job scheduler configurations'
            )
          }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('JobSchedulerConfigurations', 'Add schedule...')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('JobSchedulerConfigurations', 'Export')"
          variant="light"
          size="sm"
          :disabled="!items || !items.length"
          use-auth
          force-download
          downloadFileName="job-scheduler-configurations.json"
          :href="$fn.urlFor('export_job_scheduler_config')"
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('JobSchedulerConfigurations', 'Import')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_8116269939469002_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.JobSchedulerConfigurations"
        :editable="b_3343864469950044_editable"
        :value="items"
        :globs="{ openJobSchedulerConfig }"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
